import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useDeactivateAcquisition from "../Hooks/useDeactivateAcquisition";
import useGetAcquisition from "../Hooks/useGetAcquisition";
import {
  acquisitionConfigFetching,
  acquisitionConfigFilterText,
  acquisitionConfigReFetching,
  addStateAcquisitionConfig,
  editStateAcquisitionConfig,
  rowEditAcquisitionConfig,
  selectedRowsStateAcquisitionConfig,
} from "../atoms/atoms";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { useEffect, useState } from "react";
import { IAcquisitionConfig, IAcquisitionConfigTableData } from "../Type";
import { PERMISSIONS } from "modules/PermissionsConstants";
import Timer from "components/Timer";
import { toast } from "components/toastNotification";
import { useLocation } from "react-router-dom";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import EditButton from "components/EditButton";
import AcquisitionConfigForm from "../Form/AcquisitionConfigForm";
import TableFilters from "components/TableFilters";
import AddButton from "components/AddButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";

const ExpenseAccountTable = () => {
  const { data: directData, refetch: reFetch } = useGetAcquisition();
  const BulkDelete = useDeactivateAcquisition();

  // Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateAcquisitionConfig
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateAcquisitionConfig
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateAcquisitionConfig
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    acquisitionConfigFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditAcquisitionConfig);
  const userPermissions = useRecoilValue(permissionsSelector);

  const [isFetching, setIsFetching] = useRecoilState(acquisitionConfigFetching);
  const isReFetching = useRecoilValue(acquisitionConfigReFetching);

  // Local State
  const [tableData, setTableData] = useState<IAcquisitionConfig[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState([]);
  const [acquisitionId, setAcquisitionId] = useState("");
  const [fromRecent, setFromRecent] = useState(false);

  // Permisision States
  const addPermission =
    PERMISSIONS.permission_utilities_acquisitionconfiguration_add;
  const editPermission =
    PERMISSIONS.permission_utilities_acquisitionconfiguration_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_acquisitionconfiguration_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setAcquisitionId(recordIdFromRecent.recordId);
          setTableFilteredData(result);
          setRecent(result[0].acquisitionName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  useEffect(() => {
    if (filterTextState !== "") {
      const filteredData = directData?.data?.filter((x) =>
        x.acquisitionName
          ?.toLocaleLowerCase()
          ?.trim()
          ?.includes(filterTextState?.toLocaleLowerCase()?.trim())
      );
      setTableFilteredData(filteredData);
    }
    if (recordIdFromRecent?.recordId) {
      if (recordIdFromRecent?.recordId?.includes(".")) {
        const acquisitionIds = acquisitionId
          ?.split(".")
          ?.map((id) => Number(id?.trim()));
        const filteredData = directData?.data?.filter((x) =>
          acquisitionIds?.includes(x.acquisitionId)
        );
        setTableFilteredData(filteredData);
        setFromRecent(true);
      } else {
        const filteredData = directData?.data?.filter(
          (x) => x.acquisitionId === Number(acquisitionId)
        );
        setTableFilteredData(filteredData);
        setFromRecent(true);
      }
    }
  }, [acquisitionId, directData, filterTextState]);

  // Handle Functions
  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IAcquisitionConfigTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IAcquisitionConfigTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids = selectedRows && selectedRows.map((row) => row.acquisitionId);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {`Are you sure you want to deactivate ${rowsCount} ${
        rowsCount > 1 ? "Acquisition Names" : "Acquisition Name"
      }`}
    </div>
  );

  const matColumns = [
    {
      title: "Acquisition Name",
      field: "acquisitionName",
      customSort: (a, b) =>
        a.acquisitionName && b.acquisitionName
          ? a.acquisitionName.trim().localeCompare(b.acquisitionName.trim())
          : a.acquisitionName
          ? 0
          : -1,
    },
    {
      title: "Mexico",
      field: "usedInMexico",
      render: (rowData) => {
        return rowData?.usedInMexico ? "Yes" : "No";
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <AcquisitionConfigForm setRecent={setRecent} />
        </div>
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"acquisitionName"}
              columnDetails={"Acquisition Name"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Acquisition Name"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                fromRecent || filterTextState !== ""
                  ? tableFilteredData
                  : tableData
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Acquisition Name"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};

export default ExpenseAccountTable;
